import { Suspense } from 'react'

import { createFileRoute } from '@tanstack/react-router'

import { RouteErrorComponent } from '@/features/app-shell/route-error-component'
import { CollectorDetailSkeleton } from '@/features/integrations/collectors/components/skeletons'
import { collectorQueries } from '@/features/integrations/collectors/queries'
import { lazyNamed } from '@/utils/lazy-named'

import { getCurrentTenant } from '@/shared/auth/store'

import { ErrorBoundary } from '@/components/common/error-boundary'

const CollectorDetail = lazyNamed(
	() => import('@/features/integrations/collectors/components/detail'),
	'CollectorDetail',
)

export const Route = createFileRoute(
	'/_dashboard/integrations/collectors/$collectorId/',
)({
	loader: ({ params, context: { queryClient } }) => {
		const tenant = getCurrentTenant()

		// Prefetch what the section suspends on, so navigating from the list renders
		// a warm cache instead of waterfalling on first paint (A-07).
		if (tenant) {
			queryClient.prefetchQuery(
				collectorQueries.detail(tenant, params.collectorId),
			)
			queryClient.prefetchQuery(
				collectorQueries.collectorVolume(tenant, params.collectorId),
			)
		}
	},

	// Static, because the Collector's name is not known until the query resolves —
	// and a title that arrived one render late would be worse than a stable one.
	head: () => ({
		meta: [{ title: 'Collector | Integrations | BitLyft AIR®' }],
	}),

	component: CollectorDetailPage,
	pendingComponent: CollectorDetailSkeleton,
	errorComponent: RouteErrorComponent,
})

function CollectorDetailPage() {
	const { collectorId } = Route.useParams()

	return (
		<ErrorBoundary
			feature="integrations"
			section="collector-detail"
			entityId={collectorId}
		>
			<Suspense fallback={<CollectorDetailSkeleton />}>
				<CollectorDetail collectorId={collectorId} />
			</Suspense>
		</ErrorBoundary>
	)
}
